/**
 * Free-text Viator MCP tour search — POST /viator/mcp-search.
 * Session-only; do not persist results to toursData / plan products.
 */

import { buildAuthHeaders } from './apiClient';
import { PLANNING_API_BASE_URL } from './planningApi';
import type { ViatorProduct } from './viatorProductsService';

export function viatorMcpSearchURL(): string {
  return `${PLANNING_API_BASE_URL}/viator/mcp-search`;
}

export interface ViatorMcpSearchRequest {
  searchTerm: string;
  cityName: string;
  countryName: string;
  startDate: string;
  endDate: string;
  cityVisitId?: string | null;
  sessionId?: string | null;
  limit?: number;
}

export interface ViatorMcpSearchResponse {
  searchTerm: string;
  sessionId: string;
  cityVisitId?: string | null;
  products: ViatorProduct[];
}

function isValidProduct(value: unknown): value is ViatorProduct {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as Record<string, unknown>;
  return typeof o.productCode === 'string' && typeof o.title === 'string';
}

function normalizeProduct(raw: Record<string, unknown>): ViatorProduct | null {
  const productCode = typeof raw.productCode === 'string' ? raw.productCode : null;
  const title = typeof raw.title === 'string' ? raw.title : null;
  if (!productCode || !title) return null;

  const priceRaw = raw.price;
  let price = { currency: 'USD', startingPrice: 0 };
  if (priceRaw && typeof priceRaw === 'object' && !Array.isArray(priceRaw)) {
    const p = priceRaw as Record<string, unknown>;
    price = {
      currency: typeof p.currency === 'string' ? p.currency : 'USD',
      startingPrice: typeof p.startingPrice === 'number' ? p.startingPrice : 0,
    };
  }

  const categories = Array.isArray(raw.categories)
    ? raw.categories.filter((c): c is string => typeof c === 'string')
    : [];

  return {
    categories,
    description: typeof raw.description === 'string' ? raw.description : '',
    price,
    productCode,
    title,
    productUrl: typeof raw.productUrl === 'string' ? raw.productUrl : null,
    imageUrl: typeof raw.imageUrl === 'string' ? raw.imageUrl : null,
    isMeal: typeof raw.isMeal === 'boolean' ? raw.isMeal : null,
    matchedInterests: Array.isArray(raw.matchedInterests)
      ? raw.matchedInterests.filter((c): c is string => typeof c === 'string')
      : null,
  };
}

/** ISO-8601 local wall-clock style used by plan create / iOS MCP client. */
export function mcpSearchIsoDate(ymd: string | null | undefined, endOfDay = false): string {
  const day =
    ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd.trim())
      ? ymd.trim()
      : new Date().toISOString().slice(0, 10);
  return endOfDay ? `${day}T23:59:59Z` : `${day}T00:00:00Z`;
}

export function parseViatorMcpSearchResponse(body: unknown): ViatorMcpSearchResponse {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error('Invalid MCP search response');
  }
  const record = body as Record<string, unknown>;
  const productsRaw = Array.isArray(record.products) ? record.products : [];
  const products: ViatorProduct[] = [];
  for (const item of productsRaw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const normalized = normalizeProduct(item as Record<string, unknown>);
    if (normalized && isValidProduct(normalized)) products.push(normalized);
  }

  const sessionId =
    typeof record.sessionId === 'string' && record.sessionId.trim()
      ? record.sessionId.trim()
      : '';
  if (!sessionId) {
    throw new Error('MCP search response missing sessionId');
  }

  return {
    searchTerm: typeof record.searchTerm === 'string' ? record.searchTerm : '',
    sessionId,
    cityVisitId:
      typeof record.cityVisitId === 'string' && record.cityVisitId.trim()
        ? record.cityVisitId.trim()
        : null,
    products,
  };
}

export async function searchViatorMcp(
  request: ViatorMcpSearchRequest
): Promise<ViatorMcpSearchResponse> {
  const term = request.searchTerm.trim();
  if (!term) {
    throw new Error('searchTerm is required');
  }

  const payload = {
    searchTerm: term,
    cityName: request.cityName.trim(),
    countryName: request.countryName?.trim() ?? '',
    startDate: request.startDate,
    endDate: request.endDate,
    limit: request.limit ?? 10,
    ...(request.cityVisitId?.trim()
      ? { cityVisitId: request.cityVisitId.trim() }
      : {}),
    ...(request.sessionId?.trim() ? { sessionId: request.sessionId.trim() } : {}),
  };

  const headers = await buildAuthHeaders(true);
  const response = await fetch(viatorMcpSearchURL(), {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `MCP search failed (${response.status})`);
  }

  const json: unknown = await response.json();
  return parseViatorMcpSearchResponse(json);
}
